import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { DomesticAddressFormModel } from './form-group/domestic-address-form.model';
import { Slownik } from '../../../models/slownik';
import { SlownikRodzaj } from '../../../enums/slownik-rodzaj.enum';
import { tap } from 'rxjs';
import { ControlErrorDirective } from '../../../directive/control-error.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SlownikiApi } from '../../../services/api/slowniki.api';
import { InputMask } from 'primeng/inputmask';
import { RequiredLabelDirective } from '../../../directive/required-label.directive';
import { PrimeTemplate } from 'primeng/api';

@Component({
  selector: 'app-form-domestic-address',
  imports: [
    AutoComplete,
    FormsModule,
    InputText,
    ReactiveFormsModule,
    ControlErrorDirective,
    InputMask,
    RequiredLabelDirective,
    PrimeTemplate,
  ],
  templateUrl: './form-domestic-address.component.html',
  styleUrl: './form-domestic-address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormDomesticAddressComponent {
  public formGroup = input.required<FormGroup<DomesticAddressFormModel>>();
  public schoolAddress = input<boolean>(false);
  protected gminaOptions = signal<Slownik[]>([]);
  protected miejscowoscOptions = signal<Slownik[]>([]);
  protected uliceOptions = signal<Slownik[]>([]);
  readonly #slownikiApi$ = inject(SlownikiApi);
  readonly #destroyRef = inject(DestroyRef);
  protected readonly emptySelectionMessage = 'Nie znaleziono rekordu';

  protected onFilterGmina(event: AutoCompleteCompleteEvent): void {
    const query: string = event.query?.trim() ?? '';
    if (query.length > 10) {
      return;
    }
    if (query.length < 3) {
      this.gminaOptions.set([]); // wyczyszczenie sugestii dla krótkich zapytań
      return;
    }
    this.#slownikiApi$
      .getSlownik$(SlownikRodzaj.TERYT_GMINY, query)
      .pipe(
        tap((value: Slownik[]) => {
          this.gminaOptions.set(value);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected onFilterMiejscowosc(event: AutoCompleteCompleteEvent): void {
    const gminaDzielnica = this.formGroup()?.controls.gminaDzielnica.value;
    if (gminaDzielnica?.kod) {
      const query: string = event.query?.trim() ?? '';
      if (query.length > 12) {
        return;
      }
      if (query.length < 2) {
        return;
      }
      this.#slownikiApi$
        .getSlownik$(
          SlownikRodzaj.TERYT_MIEJSCOWOSCI,
          query,
          gminaDzielnica.kod,
        )
        .pipe(
          tap((value: Slownik[]) => {
            this.miejscowoscOptions.set(value);
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected onFilterUlica(event: AutoCompleteCompleteEvent): void {
    const miejscowosc = this.formGroup()?.controls.miejscowosc.value;
    if (miejscowosc?.kod) {
      const query: string = event.query?.trim() ?? '';
      if (query.length > 12) {
        return;
      }
      if (query.length < 2) {
        return;
      }
      this.#slownikiApi$
        .getSlownik$(SlownikRodzaj.TERYT_ULICE, query, miejscowosc.kod)
        .pipe(
          tap((value: Slownik[]) => {
            this.uliceOptions.set(value);
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected onGminaChange(): void {
    this.miejscowoscOptions.set([]);
    this.uliceOptions.set([]);
    this.formGroup()?.controls.miejscowosc.reset();
    this.formGroup()?.controls.ulica.reset();
  }

  protected onMiejscowoscChange(): void {
    this.uliceOptions.set([]);
    this.formGroup()?.controls.ulica.reset();
  }
}
